'use client'

import { useEffect, useRef } from 'react'
import classNames from 'classnames'
import { useSiteContext, useResponsive } from '@dustin-web/microsite-components/src/hooks'
import { GetMainNavigationQuery } from '@dustin-web/microsite-data/src/graphql/content/generated/graphql-types'
import { GET_MAIN_NAVIGATION } from '@dustin-web/microsite-data/src/graphql/content/get-main-navigation.graphql'
import { useHeaderContext } from './header-context'
import { getSolutionsUrl } from '@dustin-web/microsite-config'
import Link from 'next/link'
import { useGraphql } from '@dustin-web/microsite-data/src/hooks'
import Icon from '@dustin-web/microsite-components/src/icon'
import type { IconName } from '@dustin-web/microsite-components/src/icon'
import { DigitalData } from '@dustin-web/microsite-components/src/utils/digital-data'
import {
  NavigationEventType,
  NavigationSelectionType,
} from '@dustin-web/microsite-components/src/utils/digital-data-types'

type MenuItemType = NonNullable<GetMainNavigationQuery['mainNavigation']>['menuItems'][0]

type Props = {
  showMobileSolutions?: boolean
}

type SolutionsMenuVariantProps = {
  menuItems: MenuItemType[]
  isSmallScreen: boolean
  mobileMenuOpen: boolean
}

const SolutionsMenuVarient = ({
  menuItems,
  isSmallScreen,
  mobileMenuOpen,
}: SolutionsMenuVariantProps) => {
  const itemsGroups = menuItems?.map((item, i) => {
    const itemUrl = item?.url ? new URL(item.url) : null
    return (
      <div
        className={classNames('u-h-100', {
          'col-3': !isSmallScreen,
          'col-12 u-pb-8': isSmallScreen,
        })}
        key={i}>
        <div
          className={classNames({
            'c-header__solutions-col': !isSmallScreen,
            'c-mobile-header-menu__solutions-col': isSmallScreen,
          })}>
          {!!item.iconName && (
            <Icon
              name={item.iconName as IconName}
              className={classNames('u-mr-12 i-20', {
                'u-color-white': !isSmallScreen,
              })}
            />
          )}
          <div
            className={classNames({
              'col-10': !isSmallScreen,
              'col-11': isSmallScreen,
            })}>
            {itemUrl ? (
              <Link
                className={classNames({
                  'c-mobile-header-menu__solutions c-mobile-header-menu__primary-action c-mobile-header-menu__primary-action--w-sub-menu':
                    isSmallScreen,
                  'c-header__solutions-link h4 u-opacity-1 u-color-white': !isSmallScreen,
                })}
                onClick={() => {
                  DigitalData.pushNavigationInteraction(
                    NavigationEventType.MainMenu,
                    item.displayName,
                    'link_click',
                    { selectionType: NavigationSelectionType.Services }
                  )
                }}
                prefetch={false}
                href={itemUrl.pathname}>
                {item.displayName}
                {!isSmallScreen && (
                  <Icon name="arrow-right-small" className="i-arrow-right i-24 solutions-icon" />
                )}
              </Link>
            ) : (
              <span
                className={classNames({
                  'c-mobile-header-menu__solutions c-mobile-header-menu__primary-action':
                    isSmallScreen,
                  'h4 u-opacity-1 u-color-white': !isSmallScreen,
                })}>
                {item.displayName}
              </span>
            )}
            <div
              className={classNames({
                'c-header__solutions-content': !isSmallScreen,
                'c-mobile-header-menu__solutions-content col-11 u-px-0': isSmallScreen,
              })}
              dangerouslySetInnerHTML={{ __html: item.html }}
            />
          </div>
        </div>
      </div>
    )
  })

  return (
    <div
      className={classNames({
        'c-header__solutions-wrap': !isSmallScreen,
        'c-mobile-header-menu__primary-list ul-stripped': isSmallScreen,
        'c-mobile-header-menu__primary-list--upcoming': isSmallScreen && !mobileMenuOpen,
      })}>
      <div className="row u-h-100">{itemsGroups}</div>
    </div>
  )
}

const SolutionsMenu = ({ showMobileSolutions = false }: Props) => {
  const { sourcingCompany } = useSiteContext()
  const { mobile, desktop } = useHeaderContext()
  const { isSmallScreen } = useResponsive()
  const solutionListRef = useRef<HTMLDivElement>(null)
  const isActive = desktop.activeMegaMenu === 'solutions' || showMobileSolutions

  useEffect(() => {
    if (desktop.activeMegaMenu === desktop.focusMegaMenu) {
      solutionListRef.current?.focus()
    }
  }, [desktop])

  const { data, isValidating: loading } = useGraphql<GetMainNavigationQuery>(GET_MAIN_NAVIGATION, {
    variables: { uri: getSolutionsUrl(sourcingCompany) },
    context: {
      skip: !isActive,
    },
  })

  const requestFailed = !data && loading
  const { menuItems } = data?.mainNavigation ?? {}

  if (requestFailed) {
    return
  }

  const variantMenuItems = menuItems?.filter(item => item.inVariant)

  if (!variantMenuItems) {
    return
  }

  return (
    <div ref={solutionListRef} tabIndex={0}>
      <SolutionsMenuVarient
        menuItems={variantMenuItems}
        isSmallScreen={isSmallScreen}
        mobileMenuOpen={mobile.isSolutionsOpen}
      />
    </div>
  )
}

export default SolutionsMenu
